package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import dev.lingvo.shared.ShotSetApprovalDto
import dev.lingvo.site.components.localeLabel
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.dom.Div

/** The server recomputes consent validity; local render hashes never stand in for renewed approval. */
@Composable
fun SetApproval(approval: ShotSetApprovalDto?, enabled: Boolean, busy: Boolean, onApprove: () -> Unit, visible: Boolean = enabled) {
    Div(attrs = { attr("data-set-approval", ""); attr("aria-live", "polite") }) {
        if (approval != null) {
            LMuted(tf("shots.review.approved_by", "Approved by {name}", "name" to approval.approvedBy))
            if (!approval.valid) {
                val locales = approval.invalid.map { it.cell.locale }.distinct()
                LMuted(tPlural("shots.review.invalid", locales.size, "{count} language changed since: {languages}", "{count} languages changed since: {languages}",
                    "languages" to locales.joinToString(", ") { localeLabel(it, "") }))
            }
        }
        if (visible) LButton(
            if (approval == null) t("app.projects.slug.screenshots.set_approval.approve_this_set", "Approve this set") else t("app.projects.slug.screenshots.set_approval.approve_again", "Approve again"),
            onApprove, variant = LButtonVariant.SECONDARY, loading = busy, enabled = enabled && !busy,
        )
    }
}

/** Revocation retires consent in Lingvo and never implies an undo of an already delivered store set. */
@Composable
fun SetApprovalRevoke(
    approval: ShotSetApprovalDto?, canManage: Boolean, busy: Boolean, running: Boolean, error: String?, onRevoke: () -> Unit,
) {
    var confirming by androidx.compose.runtime.remember(approval?.id) { androidx.compose.runtime.mutableStateOf(false) }
    if (approval == null || !canManage) return
    LButton(t("app.projects.slug.screenshots.set_approval.revoke_set_approval", "Revoke set approval"), { confirming = true }, variant = LButtonVariant.DANGER,
        enabled = !busy && !running, loading = busy)
    if (running) LMuted(t("app.projects.slug.screenshots.set_approval.wait_for_the_running_push_to", "Wait for the running push to finish before revoking approval."))
    error?.let { dev.lingvo.site.ds.LAlert(it, variant = dev.lingvo.site.ds.LAlertVariant.DANGER, live = true) }
    dev.lingvo.site.ds.LConfirmDialog(confirming, t("app.projects.slug.screenshots.set_approval.revoke_this_sets_approval", "Revoke this set's approval?"), t("app.projects.slug.screenshots.set_approval.revoke_approval", "Revoke approval"), {
        if (!busy && !running) onRevoke()
    }, { confirming = false }, busy = busy, error = error,
        consequenceTitle = t("app.projects.slug.screenshots.set_approval.the_live_store_stays_unchanged", "The live store stays unchanged"),
        consequence = t("app.projects.slug.screenshots.set_approval.this_set_can_no_longer_authorize", "This set can no longer authorize a new push. Review and approve the set again before pushing. Revocation does not undo a completed push."))
}
